import { Button, TextField, useAlert } from '@ads/components-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { BannerLangViewModel, BannerViewModel } from '@/shared/api/generated';
import { ConfirmDialog } from '@/shared/ui/molecules/ConfirmDialog';

import { useBannerLanguageLabels } from '../../../hooks/useBannerLanguageLabels';
import { useBannerMediaFields } from '../../../hooks/useBannerMediaFields';
import { useBannerTranslations } from '../../../hooks/useBannerTranslations';
import { useCompanyById } from '../../../hooks/useCompanyById';
import { useObjectUrl } from '../../../hooks/useObjectUrl';
import { useSaveBanner } from '../../../hooks/useSaveBanner';
import {
  BANNER_LANGUAGES,
  BANNER_MEDIA_KIND,
  type BannerFormValues,
  type BannerLanguage,
  bannerFormSchema,
  bannerPortugueseGaps,
  INITIAL_BANNER_FORM_VALUES,
} from '../../../types/bannerForm';
import {
  BANNER_ERROR_ANCHORS,
  type BannerErrorAnchor,
  resolveFirstBannerErrorTarget,
  scrollToBannerError,
} from '../../../utils/bannerFormErrors';
import { mapBannerToForm } from '../../../utils/mapBannerToForm';
import { BannerFieldLabel } from '../../molecules/BannerFieldLabel';
import { BannerLanguageBar } from '../../molecules/BannerLanguageBar';
import { BannerPortugueseFirstAlert } from '../../molecules/BannerPortugueseFirstAlert';
import { BannerClassStudentsSection } from '../../organisms/BannerClassStudentsSection';
import { BannerDisplaySection } from '../../organisms/BannerDisplaySection';
import { BannerInfoSection } from '../../organisms/BannerInfoSection';
import { BannerMediaSection } from '../../organisms/BannerMediaSection';
import { BannerPreviewPanel } from '../../organisms/BannerPreviewPanel';

export interface BannerFormTemplateProps {
  /** Banner em edição; `null` na criação. */
  readonly banner: BannerViewModel | null;
  /** Traduções já cadastradas (vazio na criação). */
  readonly langs: readonly BannerLangViewModel[];
  readonly onCancel: () => void;
  /**
   * Chamado quando um banner **novo** termina de ser salvo (banner + traduções).
   * A criação sai da tela; a edição permanece nela, e por isso não passa este
   * callback.
   */
  readonly onCreated?: () => void;
}

/**
 * Formulário de Banner (criar/editar) em duas colunas: campos à esquerda,
 * preview fixo à direita — o layout do protótipo `Banner ArandU.dc.html`.
 *
 * Um único template cobre os dois modos: `banner === null` abre vazio (com
 * `Ativo` e `Exibir título` ligados); caso contrário é pré-preenchido pelo
 * `BannerViewModel` e pelas traduções.
 *
 * O conteúdo por idioma vive dentro dos próprios valores do formulário
 * (`translations`), então trocar de aba só muda quais campos são renderizados —
 * nada é desmontado e nada se perde. O salvamento acontece em duas fases: o
 * banner primeiro (para existir um `bannerId`), as traduções depois.
 *
 * Terminado o salvamento, a **criação sai da tela** (via `onCreated`) e a **edição
 * permanece** nela. Se as traduções falharem depois de o banner ter sido criado, a
 * tela continua aberta e o salvamento seguinte atualiza o banner já criado, em vez de
 * criar outro.
 */
export function BannerFormTemplate({
  banner,
  langs,
  onCancel,
  onCreated,
}: BannerFormTemplateProps) {
  const { t } = useTranslation('banner');
  const { alert } = useAlert();
  const languageLabels = useBannerLanguageLabels();
  const { save, isPending, uploadProgress } = useSaveBanner();
  const { saveTranslations } = useBannerTranslations(banner?.id);

  const [language, setLanguage] = useState<BannerLanguage>(BANNER_LANGUAGES.PtBr);
  const [companyLabel, setCompanyLabel] = useState<string | undefined>(undefined);
  const [isDiscardOpen, setIsDiscardOpen] = useState(false);
  // O salvamento tem duas fases (banner, depois traduções): a segunda não passa
  // pelo mutation do `useSaveBanner`, então o "salvando" da tela é a união das duas.
  const [isSavingTranslations, setIsSavingTranslations] = useState(false);
  // ID do banner criado nesta sessão da tela: salvar de novo atualiza esse banner
  // em vez de criar outro, já que a tela não navega depois do primeiro save.
  const [createdBannerId, setCreatedBannerId] = useState<number | undefined>(undefined);
  // Âncora do campo até onde rolar depois de um salvamento barrado pela validação.
  // Passa por estado (e não por chamada direta no handler) porque o erro pode estar
  // em outra aba de idioma: a rolagem só acontece depois de o React montar a aba.
  const [pendingErrorAnchor, setPendingErrorAnchor] = useState<BannerErrorAnchor | null>(null);
  const { companyName } = useCompanyById(banner?.companyId ?? undefined);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm<BannerFormValues>({
    resolver: zodResolver(bannerFormSchema),
    // O foco automático do RHF depende de refs nativas que os componentes ADS não
    // expõem (e o campo pode estar em outra aba de idioma): a tela rola até o erro
    // por conta própria, via `scrollToBannerError`.
    shouldFocusError: false,
    defaultValues: INITIAL_BANNER_FORM_VALUES,
  });

  const {
    videoMetadata,
    translationVideoMetadata,
    resetVideoMetadata,
    handleMediaKindChange,
    handleVideoChange,
    handleImageChange,
    handleImageDesktopChange,
  } = useBannerMediaFields({ setValue, language });

  /**
   * Banner que o formulário carregou. Salvar invalida a query e refaz o
   * `GET /api/v1/banner/{id}`, o que troca a identidade do objeto — reagir a isso
   * jogaria o formulário de volta ao que o servidor devolveu (inclusive a mídia
   * antiga, que o cadastro pode ainda não ter propagado) e apagaria o que está na
   * tela. Então o `reset` só roda quando o **banner é outro**.
   */
  const loadedBannerIdRef = useRef<number | 'create' | null>(null);
  const bannerKey = banner?.id ?? 'create';

  useEffect(() => {
    if (loadedBannerIdRef.current === bannerKey) return;
    loadedBannerIdRef.current = bannerKey;

    reset(banner ? mapBannerToForm(banner, langs) : INITIAL_BANNER_FORM_VALUES);
    resetVideoMetadata();
    setLanguage(BANNER_LANGUAGES.PtBr);
  }, [bannerKey, banner, langs, reset, resetVideoMetadata]);

  /**
   * Depois de salvar, a mídia do formulário deixa de ser "arquivo escolhido" e passa
   * a ser "mídia do cadastro": o card perde o descarte e ganha só o substituir.
   *
   * Só os campos de mídia são adotados da leitura nova — o resto do formulário é do
   * operador, e sobrescrevê-lo apagaria o que ele já tiver começado a editar.
   */
  const adoptSavedMediaRef = useRef(false);

  useEffect(() => {
    if (!adoptSavedMediaRef.current || !banner) return;
    adoptSavedMediaRef.current = false;

    const saved = mapBannerToForm(banner, langs);
    setValue('imageFile', null);
    setValue('imageDesktopFile', null);
    setValue('videoFile', null);
    setValue('videoDurationSeconds', null);
    setValue('existingImageUrl', saved.existingImageUrl);
    setValue('existingImageDesktopUrl', saved.existingImageDesktopUrl);
    setValue('existingVideoUrl', saved.existingVideoUrl);
    setValue('savedMediaKind', saved.savedMediaKind);
    resetVideoMetadata();
  }, [banner, langs, resetVideoMetadata, setValue]);

  useEffect(() => {
    if (companyName) setCompanyLabel(companyName);
  }, [companyName]);

  /**
   * Rolagem até o campo que barrou o salvamento. Roda depois do commit, então a
   * aba de idioma do erro já está montada e a âncora existe no DOM.
   */
  useEffect(() => {
    if (pendingErrorAnchor === null) return;
    scrollToBannerError(pendingErrorAnchor);
    setPendingErrorAnchor(null);
  }, [pendingErrorAnchor]);

  const values = useWatch({ control }) as BannerFormValues;
  const isPortuguese = language === BANNER_LANGUAGES.PtBr;
  const isTranslation = !isPortuguese;
  const translationKey = isTranslation ? (language as 'en-US' | 'es-ES') : null;
  const translation = translationKey ? values.translations[translationKey] : null;

  const activeImageFile = translation ? translation.imageFile : values.imageFile;
  const activeExistingImageUrl = translation
    ? translation.existingImageUrl
    : values.existingImageUrl;

  const activeImageDesktopFile = translation
    ? translation.imageDesktopFile
    : values.imageDesktopFile;
  const activeExistingImageDesktopUrl = translation
    ? translation.existingImageDesktopUrl
    : values.existingImageDesktopUrl;

  const activeVideoFile = translation ? translation.videoFile : values.videoFile;
  const activeExistingVideoUrl = translation
    ? translation.existingVideoUrl
    : values.existingVideoUrl;

  const pickedImageUrl = useObjectUrl(activeImageFile ?? null);
  const pickedVideoUrl = useObjectUrl(activeVideoFile ?? null);
  const videoUrl = pickedVideoUrl ?? activeExistingVideoUrl;

  const isVideoMode = values.mediaKind === BANNER_MEDIA_KIND.Video;
  const showClassStudents = isPortuguese && !values.general;
  const isSaving = isPending || isSavingTranslations;

  // Título e mídia que ainda faltam no cadastro em português. As traduções são
  // complemento: sem eles, o banner traduzido sairia vazio para quem cai no
  // português — então a aba de tradução avisa antes, e o salvamento fica barrado.
  const portugueseGaps = bannerPortugueseGaps(values);
  const isBlockedByPortuguese = errors.translations?.message !== undefined;
  // O cadastro só substitui mídia: trocar o tipo exige o arquivo novo (a validação
  // acusa em `videoFile`/`imageFile`, e o bloco de Mídia mostra o motivo).
  const mediaError = errors.videoFile
    ? t('banner.form.validation.videoRequiredToReplaceImage')
    : errors.imageFile
      ? t('banner.form.validation.imageRequiredToReplaceVideo')
      : undefined;
  // Mídia que falta em português para as traduções poderem ser salvas: o motivo
  // aparece no próprio bloco de Mídia, que é onde o operador resolve.
  const portugueseMediaError =
    isBlockedByPortuguese && portugueseGaps.includes('media')
      ? t('banner.form.validation.mediaRequiredForTranslation')
      : undefined;
  const imageDesktopError = errors.imageDesktopFile
    ? t('banner.form.validation.imageDesktopRequired')
    : undefined;

  /**
   * Valores do formulário depois de salvar: o arquivo enviado passou a ser a mídia
   * do cadastro, então `savedMediaKind` acompanha o tipo salvo e o arquivo do outro
   * tipo sai de vista — foi ele que a substituição no servidor deixou para trás.
   */
  const afterSaveValues = (submitted: BannerFormValues): BannerFormValues => {
    const { existingImageUrl, existingVideoUrl, ...rest } = submitted;
    const isVideo = submitted.mediaKind === BANNER_MEDIA_KIND.Video;

    return {
      ...rest,
      savedMediaKind: submitted.mediaKind,
      ...(isVideo
        ? {
            imageFile: null,
            imageDesktopFile: null,
            ...(existingVideoUrl !== undefined ? { existingVideoUrl } : {}),
          }
        : {
            videoFile: null,
            videoDurationSeconds: null,
            ...(existingImageUrl !== undefined ? { existingImageUrl } : {}),
          }),
    };
  };

  const onSubmit = handleSubmit(
    submitted => {
      const editingId = banner?.id ?? createdBannerId;

      save(submitted, editingId, {
        onSuccess: saved => {
          const savedId = saved.id ?? editingId;
          if (savedId !== undefined) setCreatedBannerId(savedId);

          const finish = () => {
            alert({
              alertType: 'success',
              title: t(
                editingId ? 'banner.form.toast.updateSuccess' : 'banner.form.toast.createSuccess',
              ),
            });
            // Criar termina o fluxo e sai da tela. Editar continua nela: o formulário
            // fica com o que acabou de ser salvo e sem o "sujo", para o operador poder
            // sair sem o aviso de descarte, e o arquivo enviado passa a ser a mídia do
            // cadastro — a substituição que acabou de acontecer no servidor.
            if (editingId === undefined) {
              onCreated?.();
              return;
            }

            reset(afterSaveValues(submitted));
            // O arquivo enviado já é a mídia do cadastro: a próxima leitura do
            // banner traz o caminho dele, e é ele que o card passa a exibir.
            adoptSavedMediaRef.current = true;
          };

          if (savedId === undefined) {
            finish();
            return;
          }

          setIsSavingTranslations(true);
          void saveTranslations(savedId, submitted)
            .then(finish)
            .catch(() => {
              alert({ alertType: 'warning', title: t('banner.form.toast.langError') });
            })
            .finally(() => setIsSavingTranslations(false));
        },
        onError: () => alert({ alertType: 'warning', title: t('banner.form.toast.saveError') }),
      });
    },
    // Submit barrado pela validação (período inválido, título vazio) precisa dizer
    // por que nada aconteceu — e levar até o campo, que pode estar fora da área
    // visível ou em outra aba de idioma.
    invalid => {
      alert({ alertType: 'warning', title: t('banner.form.toast.invalidForm') });

      const target = resolveFirstBannerErrorTarget(invalid, portugueseGaps);
      if (!target) return;

      setLanguage(target.language);
      setPendingErrorAnchor(target.anchor);
    },
  );

  const handleCancel = () => {
    if (isDirty) {
      setIsDiscardOpen(true);
      return;
    }
    onCancel();
  };

  return (
    <form className="flex flex-col gap-xxs" onSubmit={onSubmit} noValidate>
      <div className="flex flex-col items-start gap-xxs lg:flex-row">
        {/* Enquanto a request está no ar, nada de editar campos nem mexer na mídia:
            o `disabled` do fieldset alcança todo controle aninhado, e `contents`
            mantém o layout intacto. O preview fica fora: é leitura. */}
        <fieldset disabled={isSaving} className="contents">
          <div className="flex w-full min-w-0 flex-1 flex-col gap-xxs">
            <BannerLanguageBar current={language} onChange={setLanguage} />

            {isTranslation && translationKey ? (
              <>
                {/* O aviso não espera o submit: quem abre a aba de tradução com o
                    português incompleto já sabe que precisa voltar primeiro. */}
                <BannerPortugueseFirstAlert gaps={portugueseGaps} />

                <div className="flex flex-col gap-mini rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface px-xxs py-xxs shadow-level1">
                  <div className="flex flex-col gap-quark">
                    <BannerFieldLabel
                      label={t('banner.form.info.title')}
                      info={t('banner.form.language.titleApiGap')}
                    />
                    <TextField
                      value={values.title}
                      onChange={() => undefined}
                      size="md"
                      fullWidth
                      disabled
                    />
                  </div>

                  <div
                    className="flex flex-col gap-quark"
                    data-field-error={BANNER_ERROR_ANCHORS.TranslationUrl}
                  >
                    <BannerFieldLabel
                      label={t('banner.form.info.url')}
                      info={t('banner.form.info.urlHint')}
                    />
                    <TextField
                      placeholder={t('banner.form.info.urlPlaceholder')}
                      value={translation?.url ?? ''}
                      type="url"
                      onChange={e =>
                        setValue(`translations.${translationKey}.url`, e.target.value, {
                          shouldDirty: true,
                        })
                      }
                      size="md"
                      fullWidth
                      hasError={Boolean(errors.translations?.[translationKey]?.url)}
                      {...(errors.translations?.[translationKey]?.url
                        ? { helper: t('banner.form.validation.urlInvalid') }
                        : {})}
                    />
                  </div>
                </div>

                {/* A tradução sobe o mesmo tipo de mídia do cadastro em português:
                    com imagem, uma arte; com vídeo, um MP4. Por isso o seletor fica
                    escondido aqui (`variant="translation"`) — quem manda é o pt-BR. */}
                <BannerMediaSection
                  bannerTitle={values.title}
                  mediaKind={values.mediaKind}
                  onMediaKindChange={handleMediaKindChange}
                  imageFile={translation?.imageFile ?? null}
                  existingImageUrl={activeExistingImageUrl}
                  onImageChange={handleImageChange}
                  imageDesktopFile={activeImageDesktopFile ?? null}
                  existingImageDesktopUrl={activeExistingImageDesktopUrl}
                  onImageDesktopChange={handleImageDesktopChange}
                  isUploading={isSaving}
                  uploadProgress={uploadProgress}
                  videoFile={translation?.videoFile ?? null}
                  existingVideoUrl={activeExistingVideoUrl}
                  videoMetadata={translationVideoMetadata}
                  onVideoChange={handleVideoChange}
                  variant="translation"
                  mediaError={mediaError}
                />
              </>
            ) : (
              <>
                {/* Na aba do português o aviso só aparece depois de um salvamento
                    barrado: num cadastro novo, sem tradução nenhuma, não há nada
                    a avisar. */}
                {isBlockedByPortuguese ? (
                  <BannerPortugueseFirstAlert gaps={portugueseGaps} />
                ) : null}

                <BannerInfoSection
                  control={control}
                  errors={errors}
                  companyLabel={companyLabel}
                  onCompanyLabelChange={setCompanyLabel}
                  videoDurationSeconds={isVideoMode ? values.videoDurationSeconds : null}
                />

                <BannerDisplaySection control={control} />

                {showClassStudents ? (
                  <BannerClassStudentsSection
                    classStudents={values.classStudents}
                    onAdd={classStudent =>
                      setValue('classStudents', [...values.classStudents, classStudent], {
                        shouldDirty: true,
                      })
                    }
                    onRemove={classStudentId =>
                      setValue(
                        'classStudents',
                        values.classStudents.filter(cs => cs.classStudentId !== classStudentId),
                        { shouldDirty: true },
                      )
                    }
                    onAddMany={added => {
                      // Turma já vinculada não entra de novo: o que a marca traz
                      // complementa a lista, não a substitui.
                      const known = new Set(values.classStudents.map(cs => cs.classStudentId));
                      setValue(
                        'classStudents',
                        [
                          ...values.classStudents,
                          ...added.filter(cs => !known.has(cs.classStudentId)),
                        ],
                        { shouldDirty: true },
                      );
                    }}
                    onRemoveBrand={brandId =>
                      setValue(
                        'classStudents',
                        values.classStudents.filter(cs =>
                          brandId === undefined ? cs.brandId === undefined : cs.brandId !== brandId,
                        ),
                        { shouldDirty: true },
                      )
                    }
                    hasError={Boolean(errors.classStudents)}
                  />
                ) : null}

                <BannerMediaSection
                  bannerTitle={values.title}
                  mediaKind={values.mediaKind}
                  onMediaKindChange={handleMediaKindChange}
                  imageFile={values.imageFile}
                  existingImageUrl={activeExistingImageUrl}
                  onImageChange={handleImageChange}
                  imageDesktopFile={values.imageDesktopFile}
                  existingImageDesktopUrl={activeExistingImageDesktopUrl}
                  onImageDesktopChange={handleImageDesktopChange}
                  isUploading={isSaving}
                  uploadProgress={uploadProgress}
                  videoFile={values.videoFile}
                  existingVideoUrl={activeExistingVideoUrl}
                  videoMetadata={videoMetadata}
                  onVideoChange={handleVideoChange}
                  mediaError={mediaError ?? portugueseMediaError}
                  imageDesktopError={imageDesktopError}
                />
              </>
            )}
          </div>
        </fieldset>

        <BannerPreviewPanel
          imageUrl={isVideoMode ? undefined : (pickedImageUrl ?? activeExistingImageUrl)}
          videoUrl={isVideoMode ? videoUrl : undefined}
          overlayTitle={
            values.showTitle ? values.title || t('banner.form.preview.fallbackTitle') : undefined
          }
          languageLabel={languageLabels[language]}
          isVideoMode={isVideoMode}
          videoDurationSeconds={values.videoDurationSeconds}
          durationSeconds={values.duration}
          startDate={values.startDate}
          endDate={values.endDate}
          url={values.url}
          isGeneral={values.general}
          classStudentCount={values.classStudents.length}
        />
      </div>

      {/* Enquanto salva, nada de segundo submit nem de sair pela metade: os dois
          botões ficam inativos até as duas fases do salvamento terminarem. */}
      <div className="flex items-center gap-nano">
        <Button
          type="submit"
          hierarchy="primary"
          size="lg"
          isLoading={isSaving}
          disabled={isSaving}
        >
          {t('banner.actions.save')}
        </Button>
        <Button
          type="button"
          hierarchy="secondary"
          size="lg"
          onClick={handleCancel}
          disabled={isSaving}
        >
          {t('banner.actions.cancel')}
        </Button>
      </div>

      <ConfirmDialog
        open={isDiscardOpen}
        tone="danger"
        title={t('banner.form.discard.title')}
        description={t('banner.form.discard.body')}
        confirmLabel={t('banner.form.discard.confirm')}
        cancelLabel={t('banner.form.discard.cancel')}
        closeAriaLabel={t('banner.form.discard.close')}
        onConfirm={() => {
          setIsDiscardOpen(false);
          onCancel();
        }}
        onClose={() => setIsDiscardOpen(false)}
      />
    </form>
  );
}
